import React, {useEffect, useMemo, useState, Fragment} from 'react';
import {useLazyQuery, useQuery} from '@apollo/react-hooks';
import {
  Button,
  HTMLSelect,
  FormGroup,
  Position,
  NonIdealState,
  Tooltip,
} from '@blueprintjs/core';
import {DateRangeInput} from '@blueprintjs/datetime';
import {useParams} from 'react-router-dom';
import {gql} from 'apollo-boost';
import moment from 'moment';

import {Breadcrumbs, Loading, SortableTableHead, compareColumns} from 'worksource-ui';
import {bugsnagClient} from 'utils/bugsnag';
import {showGQLError} from 'utils/toaster';
import HeaderBar from 'components/header-bar';
import withMainLayout from 'layouts/main/view';

const ANALYTICS_QUERY = gql`
  query FetchAnalytics(
    $clientId: ID!,
    $guideIds: [ID!]!,
    $startDate: ISO8601DateTime!,
    $endDate: ISO8601DateTime!,
  ) {
    fileAnalytics(
      clientId: $clientId,
      guideIds: $guideIds,
      startDate: $startDate,
      endDate: $endDate
    ) {
      file {
        id,
        name,
        identifier,
      },
      favoriteCount,
      openCount,
      shareCount
    }
  }
`;

const GUIDES_QUERY = gql`
  query FetchGuidesForAnalytics($clientId: ID!) {
    guides(clientId: $clientId) {
      id,
      name,
    }
  }
`;

const AnalyticsPage = () => {
  ////
  // Hooks
  //
  const {clientId} = useParams();
  const [selectedGuideId, setSelectedGuideId] = useState();
  const [currentSort, setCurrentSort] = useState({column: 'openCount', direction: 'desc'});
  const [reportRange, setReportRange] = useState(() => ({
    startDate: moment().subtract(12, 'months').toDate(),
    endDate: moment().subtract(1, 'day').toDate(),
  }));
  const {data: guidesResp, loading: isLoadingGuides} = useQuery(GUIDES_QUERY, {
    variables: {clientId},
    onError: error => showGQLError({error}),
  });

  const [fetchAnalytics, {
    data: analyticsResp,
    loading: isLoadingAnalytics,
  }] = useLazyQuery(ANALYTICS_QUERY, {
    fetchPolicy: 'network-only',
    onError: (error) => {
      bugsnagClient.notify(error);
      showGQLError({error});
    },
  });

  useEffect(() => {
    if (!selectedGuideId || !reportRange.startDate || !reportRange.endDate) return;

    fetchAnalytics({
      variables: {
        clientId,
        guideIds: [selectedGuideId],
        startDate: moment(reportRange.startDate).format('YYYY-MM-DD'),
        endDate: moment(reportRange.endDate).format('YYYY-MM-DD'),
      },
    });
  }, [selectedGuideId, reportRange.startDate, reportRange.endDate]);

  const files = useMemo(() => {
    if (!analyticsResp || !analyticsResp.fileAnalytics) return undefined;

    return analyticsResp.fileAnalytics
      .map(line => ({
        fileId: line.file.id,
        fileName: line.file.name.trim(),
        fileIdentifier: line.file.identifier,
        shareCount: line.shareCount,
        favoriteCount: line.favoriteCount,
        openCount: line.openCount,
      }))
      .sort(compareColumns(currentSort.column, currentSort.direction));
  }, [analyticsResp, currentSort.column, currentSort.direction]);

  ////
  // Events
  //
  const onSelectGuide = (e) => {
    if (e.target.value === '__select') return;
    setSelectedGuideId(e.target.value);
  };

  const onChangeDate = ([startDate, endDate]) => {
    setReportRange({startDate, endDate});
  };

  const exportCsv = () => {
    const fileRows = files.map(file => ([
      file.fileName,
      file.fileIdentifier,
      file.favoriteCount,
      file.openCount,
      file.shareCount,
    ]));
    const rows = [
      ['File name', 'Part #', 'Favorites (all-time)', 'Opens', 'Shares'],
      ...fileRows,
    ];

    const csv = rows
      .map(row => row
        .map(val => (val === null ? '' : String(val)))
        .map(val => val.replaceAll('"', '""'))
        .map(val => `"${val}"`)
        .join(','))
      .join('\r\n');

    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);

    const el = document.createElement('a');
    el.href = url;

    const startDate = moment(reportRange.startDate).format('YYYY-MM-DD');
    const endDate = moment(reportRange.endDate).format('YYYY-MM-DD');
    el.setAttribute('download', `WorkSource file analytics ${startDate} to ${endDate}`);
    el.click();
  };

  ////
  // Render
  //
  return (
    <Fragment>
      <HeaderBar>
        <div className="analytics--breadcrumbs">
          <Breadcrumbs>
            <Breadcrumbs.Breadcrumb
              href={`/clients/${clientId}/analytics/files`}
              icon="scatter-plot"
              text="Analytics"
            />
            <Breadcrumbs.Breadcrumb
              href={`/clients/${clientId}/analytics/files`}
              icon="scatter-plot"
              text="Files"
            />
          </Breadcrumbs>
        </div>
      </HeaderBar>
      <div className="analytics--filter-chrome">
        <div className="content-container">
          <FormGroup label="Guide">
            <HTMLSelect
              disabled={isLoadingGuides}
              onChange={onSelectGuide}
              value={selectedGuideId}
            >
              {isLoadingGuides && (
                <option>Loading guides...</option>
              )}
              {guidesResp?.guides && (
                <option value="__select">Select a guide</option>
              )}
              {guidesResp?.guides.map(guide => (
                <option key={guide.id} value={guide.id}>{guide.name}</option>
              ))}
            </HTMLSelect>
          </FormGroup>
          <FormGroup label="Report range">
            <DateRangeInput
              formatDate={date => moment(date).format('YYYY-MM-DD')}
              parseDate={date => moment(date).toDate()}
              value={[reportRange.startDate, reportRange.endDate]}
              onChange={onChangeDate}
              maxDate={moment().toDate()}
              minDate={moment().subtract(2, 'years').toDate()}
              popoverProps={{
                position: Position.BOTTOM_RIGHT,
                tetherOptions: {
                  constraints: [
                    {to: 'scrollParent', attachment: 'together'},
                  ],
                },
              }}
            />
          </FormGroup>
          <div className="spacer" />
          <Button
            text="Export CSV"
            icon="export"
            onClick={exportCsv}
            disabled={!files || files?.length === 0}
          />
        </div>
      </div>
      <div className="content-container">
        <div className="layout--full">
          <table className="analytics--table bp3-html-table bp3-html-table-striped sortable">
            <thead>
              <tr>
                <SortableTableHead
                  label="File name"
                  column="fileName"
                  currentSort={currentSort}
                  setCurrentSort={setCurrentSort}
                />
                <SortableTableHead
                  label="Part #"
                  column="fileIdentifier"
                  currentSort={currentSort}
                  setCurrentSort={setCurrentSort}
                />
                <SortableTableHead
                  label={(
                    <Tooltip
                      hoverOpenDelay={50}
                      content={(
                        <div style={{width: 300}}>
                          We do not collect information about when files are
                          marked as favorite. This metric will remain constant
                          regardless of the report range.
                        </div>
                      )}
                    >
                      Favorites (all-time)
                    </Tooltip>
                  )}
                  column="favoriteCount"
                  className="analytics--stat-cell"
                  currentSort={currentSort}
                  setCurrentSort={setCurrentSort}
                />
                <SortableTableHead
                  label="Opens"
                  column="openCount"
                  className="analytics--stat-cell"
                  currentSort={currentSort}
                  setCurrentSort={setCurrentSort}
                />
                <SortableTableHead
                  label="Shares"
                  column="shareCount"
                  className="analytics--stat-cell"
                  currentSort={currentSort}
                  setCurrentSort={setCurrentSort}
                />
              </tr>
            </thead>
            <tbody>
              {isLoadingAnalytics && (
                <tr>
                  <td colSpan="5">
                    <NonIdealState
                      icon={<Loading />}
                      title="Loading analytics..."
                    />
                  </td>
                </tr>
              )}
              {!isLoadingAnalytics && !analyticsResp?.fileAnalytics.length && (
                <tr>
                  <td colSpan="5">
                    {!selectedGuideId && (
                      <NonIdealState
                        icon="search"
                        title="Select a guide to begin"
                      />
                    )}
                    {selectedGuideId && (
                      <NonIdealState
                        icon="document"
                        title="No results"
                      />
                    )}
                  </td>
                </tr>
              )}
              {!isLoadingAnalytics && files?.map((line) => {
                const truncatedFileName = line.fileName.length > 40
                  ? `${line.fileName.slice(0, 40)}...`
                  : line.fileName;
                const truncatedIdentifier = line.fileIdentifier?.length > 40
                  ? `${line.fileIdentifier.slice(0, 40)}...`
                  : line.fileIdentifier;

                return (
                  <tr key={line.fileId}>
                    <td><span title={line.fileName}>{truncatedFileName}</span></td>
                    <td><span title={truncatedIdentifier}>{truncatedIdentifier}</span></td>
                    <td className="analytics--stat-cell">{line.favoriteCount}</td>
                    <td className="analytics--stat-cell">{line.openCount}</td>
                    <td className="analytics--stat-cell">{line.shareCount}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Fragment>
  );
};

export default withMainLayout()(AnalyticsPage);
